import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Button from '@components/Button';
import { Input } from '@components/Input';
import { ExternalLink } from '@components/Link';
import { ModalConfirm } from '@components/Modal';
import { SectionTitle, Hint } from '@features/ChannelsForms/FormSection';
import { useCopyToClipboard, useThrottle, useTranslation } from '@hooks';
import { alertActions, channelsActions } from '@actions';
import { channelsService } from '@services';
import { handleApiError } from '@helpers/api';
import { ExternalRoutes } from '@constants/routes';

const SigningSecret = ({ liveChatId }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const secret = useSelector(
        state =>
            state.channels.data.find(channel => channel.id === liveChatId)?.liveChatParameters?.signingSecret || '',
    );
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const { onCopy } = useCopyToClipboard();
    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);

    const issueSecret = async () => {
        setBusy(true);
        try {
            await channelsService.rotateLiveChatSigningSecret(liveChatId);
            setConfirmOpen(false);
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            await dispatch(channelsActions.get());
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <SectionTitle sx={{ marginBottom: '8px' }}>{t('SettingsChannels.signedInCustomers')}</SectionTitle>
            <Hint sx={{ marginBottom: '8px' }}>
                {t('SettingsChannels.signingSecretHint')}{' '}
                <ExternalLink href={ExternalRoutes.LearnMore[i18n.language].liveChatWebsite} underline="hover">
                    {t('common.learnMore')}
                </ExternalLink>
            </Hint>
            <Input
                inputProps={{ value: secret }}
                label={t('SettingsChannels.signingSecret')}
                fullWidth
                disabled
                onAddonClick={() => onCopyClick(secret)}
                addonComponent={
                    secret ? <ContentCopyIcon sx={{ fontSize: 18, color: 'var(--text-secondary)' }} /> : null
                }
            />
            <Button
                variant="outlined"
                disabled={!liveChatId || busy}
                onClick={secret ? () => setConfirmOpen(true) : issueSecret}
            >
                {t(secret ? 'buttons.replaceSecret' : 'buttons.generateSecret')}
            </Button>
            {confirmOpen ? (
                <ModalConfirm
                    isOpen
                    aboveModal
                    handleClose={() => setConfirmOpen(false)}
                    title={t('SettingsChannels.replaceSecretTitle')}
                    description={t('SettingsChannels.replaceSecretBody')}
                    onPrimaryAction={issueSecret}
                    primaryActionText={t('buttons.replaceSecret')}
                    primaryActionType="danger"
                />
            ) : null}
        </>
    );
};

export default SigningSecret;
